import React, { useState, useEffect } from 'react'
import { makeStyles, Theme } from '@material-ui/core/styles'
import clsx from 'clsx'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ButtonBase,
} from '@material-ui/core'
import { sortTable } from '@utils/sortTable'
import { useUserSession } from '@utils/useUserSession'
import { Sort } from '@src/types/graphql-types'
import DataTableSortByIcon from './DataTableSortByIcon'
import { visuallyHidden } from '@src/styles/utils/visibility'
import { useIntl } from 'react-intl'

//TODO: Get rid of anys to increase type safety. Could get rid of the default
//or default T to unknown. Any in use now to avoid updating the types for all
//other instances of DataTable.

export interface DataColumn<T = any> {
  headElement?: JSX.Element
  headElementEndIcon?: JSX.Element
  bodyElement: (
    data: T,
    noLink?: boolean | undefined,
    styleClasses?: string
  ) => React.ReactNode
  width?: string | number
  textAlign?: 'center' | 'inherit' | 'justify' | 'left' | 'right'
  padding?: 'checkbox' | 'default' | 'normal' | 'none'
  sortBy?: string
  dataScrub?: boolean
  conditionalRender?: boolean
  title?: string
}

export type DataRowProps<T> = {
  currentValue: T
  columns: DataColumn<T>[]
  afterRow?: (data: T) => JSX.Element | null
  rowSelected?: (currentValue: T) => boolean
  onRowClick?: (
    currentValue: T,
    e: React.MouseEvent<HTMLTableRowElement>
  ) => void
  verticalAlign?: 'top' | 'middle' | 'bottom' | 'baseline'
  highlightRow: boolean
}

interface TableProps<T = any> {
  data: T[]
  columns: DataColumn<T>[]
  captionText: JSX.Element | string // hidden text for screen readers to provide a brief table description
  summaryText?: JSX.Element | string // hidden text for screen readers to help users navigate it
  noDataComponent?: JSX.Element
  afterRow?: (data: T) => JSX.Element | null
  rowSelected?: (currentValue: T) => boolean
  onColumnSort?: (sortColumn: string | undefined) => void
  onRowClick?: (
    currentValue: T,
    e: React.MouseEvent<HTMLTableRowElement>
  ) => void
  initialSortColumn?: string
  initialSortDirection?: Sort
  verticalAlign?: 'top' | 'middle' | 'bottom' | 'baseline'
  className?: string
  renderRow?: (props: DataRowProps<T>) => React.ReactNode
  highlightRow?: boolean
  keyExtractor?: (currentValue: T, index: number) => string | number
  tableContainerClass?: string
}

const useStyles = makeStyles((theme: Theme) => ({
  table: {
    display: 'block',
    '& .MuiTableCell-root': {
      padding: `${theme.spacing(2)}px ${theme.spacing(2)}px`,
      border: 'none',
    },
    '& .MuiTableCell-root.MuiTableCell-paddingCheckbox .MuiFormControlLabel-root':
      {
        margin: 0,
        marginLeft: -9,
      },

    [theme.breakpoints.up('md')]: {
      display: 'table',
      tableLayout: 'fixed',
      borderCollapse: 'collapse',
      borderSpacing: '0 2px',
      '& .MuiTableCell-root': {
        padding: `${theme.spacing(4)}px ${theme.spacing(2)}px`,
      },
    },
  },
  tableHead: {
    display: 'none',
    borderTop: `1px solid ${theme.palette.grey[400]}`,
    background: theme.palette.background.grey,
    '& .MuiTableCell-head': {
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),
      fontWeight: theme.typography.fontWeightBold,
    },
    [theme.breakpoints.up('md')]: {
      display: 'table-header-group',
    },
  },
  headElement: {
    display: 'inline-flex',
    border: 0,
    padding: 0,
    background: 'transparent',
    alignItems: 'center',
  },
  headLabel: {
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 900,
    color: theme.palette.primary.main,
  },
  headUnsortable: {
    color: theme.palette.common.black,
  },
  sortByIcon: {
    marginLeft: theme.spacing(2),
  },
  tableBody: {
    display: 'block',
    '& .MuiTableRow-root': {
      display: 'block',
      padding: theme.spacing(4, 0),
      width: '100%',
      backgroundColor: theme.palette.background.default,
      borderTop: `1px solid ${theme.palette.grey[400]}`,
      '&.Mui-selected': {
        backgroundColor: theme.palette.primary.light,
      },
      '& .MuiTableCell-root': {
        borderStyle: 'none',
        wordBreak: 'break-word',
        fontSize: theme.typography.pxToRem(15),
        display: 'block',
      },
    },
    '& tr:last-child': {
      borderBottom: `1px solid ${theme.palette.grey[400]}`,
    },
    [theme.breakpoints.up('md')]: {
      display: 'table-row-group',
      '& .MuiTableRow-root': {
        display: 'table-row',
        padding: 0,
        backgroundColor: theme.palette.background.default,
        borderTop: `1px solid ${theme.palette.grey[400]}`,
        '&.Mui-selected': {
          backgroundColor: theme.palette.primary.light,
        },
        '& .MuiTableCell-root.MuiTableCell-body': {
          display: 'table-cell',
        },
      },
    },
  },
  cursorPointer: {
    cursor: 'pointer',
  },
  mobileItemHeader: {
    fontWeight: 600,
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  mobileLeftAlign: {
    [theme.breakpoints.down('sm')]: {
      textAlign: 'left',
    },
  },
  tableRowHeader: {
    verticalAlign: 'baseline',
  },
  tableRowHighlight: {
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
    },
  },
  visuallyHidden,
}))

export const DataRow = function <T>({
  columns,
  afterRow,
  onRowClick,
  rowSelected,
  verticalAlign,
  currentValue,
  highlightRow = false,
}: DataRowProps<T>) {
  const classes = useStyles()
  return (
    <>
      <TableRow
        //If we want universal hover state we can remove the highlightRow prop and just pass in classes.tableRowHighlight
        className={clsx(
          onRowClick && classes.cursorPointer,
          highlightRow && classes.tableRowHighlight
        )}
        selected={rowSelected && rowSelected(currentValue)}
        onClick={(e) => onRowClick && onRowClick(currentValue, e)}
      >
        {columns.map((column, index) => {
          const columnName = column.headElement
          const columnBody = column.bodyElement(currentValue)

          return (
            <TableCell
              key={index}
              align={column.textAlign || 'inherit'}
              style={{ verticalAlign }}
              padding={column.padding || 'normal'}
              className={classes.mobileLeftAlign}
              data-scrub={column?.dataScrub ? true : undefined} //data-scrub is used to masking PII fields
            >
              <div className={classes.mobileItemHeader}>{columnName}</div>
              {columnBody}
            </TableCell>
          )
        })}
      </TableRow>
      {afterRow && afterRow(currentValue)}
    </>
  )
}

const defaultRenderRow = function <T>(props: DataRowProps<T>) {
  return <DataRow {...props} />
}

/**
 * Renders a table of given data with sortable columns
 *
 * Can be paired with ResponsiveDataAccordion for a better mobile experience
 */
const DataTable = <T extends {} | null>({
  data,
  columns,
  captionText,
  summaryText,
  noDataComponent,
  afterRow,
  rowSelected,
  onColumnSort,
  onRowClick,
  initialSortColumn,
  initialSortDirection,
  verticalAlign = 'middle',
  className,
  renderRow = defaultRenderRow,
  keyExtractor = (currentValue, index) => index,
  // If in the future we decide we want highligted rows on hover everywhere we can remove this.
  highlightRow = false,
  tableContainerClass,
}: TableProps<T>) => {
  const classes = useStyles()
  const { userSession } = useUserSession()
  const [sortBy, setSortBy] = useState<[Sort, string]>([
    initialSortDirection || Sort.Nameasc,
    initialSortColumn || '',
  ])
  const currentColumn = sortBy[1]
  const sortDirection = sortBy[0]
  const sortedData = sortTable(sortBy, userSession.language, data)
  const [sortDirectionMessage, setSortDirectionMessage] = useState('Ascending')
  const [sortColumnName, setSortColumnName] = useState('')
  const { formatMessage } = useIntl()

  const sortableTranslated = formatMessage({
    id: 'SORTABLE',
    defaultMessage: 'sortable',
  })
  const sortedByTranslated = formatMessage({
    id: 'SORTED_BY',
    defaultMessage: 'Sorted by',
  })
  const ascendingTranslated = formatMessage({
    id: 'ASCENDING',
    defaultMessage: 'Ascending',
  })
  const descendingTranslated = formatMessage({
    id: 'DESCENDING',
    defaultMessage: 'Descending',
  })

  const handleSortBy = (
    sortColumn: string | undefined,
    label?: string | undefined
  ) => {
    let newSortDirection = sortDirection
    let newSortColumn = currentColumn

    // If sorting by the same column, toggle sort direction
    if (sortColumn === currentColumn) {
      newSortDirection =
        sortDirection === Sort.Nameasc ? Sort.Namedesc : Sort.Nameasc
    } else {
      // Otherwise, set new column and default direction.
      newSortColumn = sortColumn || ''
      newSortDirection = Sort.Namedesc
      label && setSortColumnName(label)
    }

    // Update the sorting state
    setSortBy([newSortDirection, newSortColumn])

    // Use the callback function to handle sorting data (if applicable)
    if (onColumnSort) {
      onColumnSort(newSortColumn) // Pass the new sorted column
    }
  }

  // Adding some accessibility messaging on sort order when it changes
  useEffect(() => {
    sortColumnName &&
      setSortDirectionMessage(
        `${sortedByTranslated} ${sortColumnName} ${
          sortDirection === Sort.Nameasc
            ? ascendingTranslated
            : descendingTranslated
        }`
      )
  }, [sortBy])

  return (
    <TableContainer className={tableContainerClass}>
      {sortColumnName && (
        <div aria-live={'polite'} className={classes.visuallyHidden}>
          {sortDirectionMessage}
        </div>
      )}
      <Table
        className={clsx(classes.table, className)}
        aria-labelledby="table-caption"
      >
        <caption className={classes.visuallyHidden} id="table-caption">
          {captionText}{' '}
          <span>
            {summaryText} {sortBy && sortableTranslated}
          </span>
        </caption>
        <colgroup>
          {columns.map((column, index) => {
            return column.width ? (
              <col key={index} width={column.width} />
            ) : (
              <col key={index} />
            )
          })}
        </colgroup>
        <TableHead className={classes.tableHead}>
          <TableRow className={classes.tableRowHeader}>
            {columns.map((column, index) => (
              <TableCell
                key={index}
                align={column.textAlign || 'inherit'}
                padding={column.padding || 'normal'}
                className={classes.mobileLeftAlign}
              >
                {!column.sortBy ? (
                  <div className={classes.headElement}>
                    <div
                      className={clsx(
                        classes.headLabel,
                        classes.headUnsortable
                      )}
                    >
                      {column.headElement}
                    </div>
                  </div>
                ) : (
                  <ButtonBase
                    className={classes.headElement}
                    onClick={() => handleSortBy(column.sortBy, column?.title)}
                    style={{
                      textAlign:
                        column.textAlign === 'center'
                          ? 'center'
                          : column.textAlign === 'left'
                            ? 'left'
                            : column.textAlign === 'right'
                              ? 'right'
                              : 'inherit',
                    }}
                  >
                    <div className={classes.headLabel}>
                      {column.headElement}
                    </div>
                    {currentColumn === column.sortBy && (
                      <div className={classes.sortByIcon}>
                        <DataTableSortByIcon sortBy={sortBy} />
                      </div>
                    )}
                  </ButtonBase>
                )}
                {column.headElementEndIcon}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody className={classes.tableBody}>
          {sortedData.length === 0 && (
            <TableRow>
              <TableCell colSpan={columns.length}>{noDataComponent}</TableCell>
            </TableRow>
          )}
          {sortedData.length > 0 &&
            sortedData.map((currentValue, index) => (
              <React.Fragment key={keyExtractor(currentValue, index)}>
                {renderRow({
                  columns,
                  afterRow,
                  onRowClick,
                  rowSelected,
                  verticalAlign,
                  currentValue,
                  highlightRow,
                })}
              </React.Fragment>
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default DataTable
